import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Save, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { CopyableInput } from "../../components/CopyableInput";
import { adminApi } from "../../lib/api";
import { JOB_STATUSES, INVOICE_STATUSES, PAYMENT_TYPES } from "../../lib/jobConstants";
import { calcJob, money } from "../../lib/jobCalc";
import { CustomerPicker } from "../../components/admin/CustomerPicker";
import { ClientLookupCard } from "../../components/admin/ClientLookupCard";
import { BrokerPicker } from "../../components/admin/BrokerPicker";
import { normalizeTime } from "../../lib/timeUtils";

const blank = {
  customer_id: null, customer_code: null,
  broker_id: null, client_id: null, vehicle_id: null,
  client_name: "", client_title_surname: "", client_id_number: "",
  contact_mobile: "", contact_home: "", contact_work: "", email: "",
  fitment_address: "", fitment_gps: "",
  vehicle_make_model: "", vehicle_year: "", registration: "",
  engine_number: "", vin_number: "", part_code: "",
  payment_type: "insurance",
  brand: "dd", // 'dd' | 'bsg' — controls letterhead logo on ALL paperwork for this job
  insurance_company: "", broker: "", broker_contact: "", broker_fax: "",
  authorized_by: "", policy_number: "", claim_number: "",
  date_of_loss: "", cause_of_loss: "",
  damage_locations: [], claim_actions: [], flatglass_items: [],
  fitment_date: "", fitment_time: "", fitter_team_leader: "", assistant: "",
  cost: "", retail: "", fitment_fee: "", moulding: "",
  discount_pct: "", excess_pct: "", excess_amount_override: "", vat_pct: 15,
  vat_mode: "exclusive", extra_line_items: [],
  order_number: "", invoice_sent_to: "",
  invoice_status: "pending", payment_received: false,
  status: "new", notes: "",
};

const inputCls =
  "bg-white border-[#cfd7db] text-[#111619] placeholder:text-[#9aa6ad] focus:border-[color:var(--brand-accent)] focus:ring-1 focus:ring-[color:var(--brand-accent)] rounded-md h-10";

// --- Claim-form pickers (damage location / action / loss cause) ---
const DAMAGE_LOCATION_OPTIONS = [
  { code: "windscreen", label: "Windscreen" },
  { code: "rear_window", label: "Rear window" },
  { code: "cabslider", label: "Cabslider" },
  { code: "door_driver_front", label: "Door glass — driver front" },
  { code: "door_driver_rear", label: "Door glass — driver rear" },
  { code: "door_pass_front", label: "Door glass — pass. front" },
  { code: "door_pass_rear", label: "Door glass — pass. rear" },
  { code: "flatglass", label: "Flatglass" },
];
const ACTION_OPTIONS = [
  { code: "replace", label: "Replace" },
  { code: "repair", label: "Repair" },
];
const LOSS_CAUSE_PRESETS = ["Stone damage", "Theft / Smash & Grab"]; // "Other" is a third option

const CheckboxChip = ({ checked, onClick, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center gap-2 px-3 h-9 rounded-md border text-sm transition ${
      checked
        ? "bg-[color:var(--brand-accent)] text-white border-[color:var(--brand-accent)] shadow-sm"
        : "bg-white text-[#3f4b52] border-[#cfd7db] hover:border-[color:var(--brand-accent)] hover:text-[color:var(--brand-accent)]"
    }`}
  >
    <span
      className={`inline-block w-4 h-4 rounded-sm border ${
        checked ? "bg-white border-white" : "border-[#9aa6ad]"
      }`}
      aria-hidden
    >
      {checked && (
        <svg viewBox="0 0 16 16" className="w-4 h-4 text-[color:var(--brand-accent)]" fill="currentColor">
          <path d="M6.5 11.5L3 8l1.4-1.4 2.1 2.1 5.1-5.1L13 5z" />
        </svg>
      )}
    </span>
    {label}
  </button>
);

const DamageLocationPicker = ({ value = [], onChange }) => {
  const toggle = (code) =>
    onChange(value.includes(code) ? value.filter((v) => v !== code) : [...value, code]);
  return (
    <div className="flex flex-wrap gap-2" data-testid="damage-location-picker">
      {DAMAGE_LOCATION_OPTIONS.map((o) => (
        <CheckboxChip
          key={o.code}
          checked={value.includes(o.code)}
          onClick={() => toggle(o.code)}
          label={o.label}
          testid={`damage-loc-${o.code}`}
        />
      ))}
    </div>
  );
};

const ActionPicker = ({ value = [], onChange }) => {
  const toggle = (code) =>
    onChange(value.includes(code) ? value.filter((v) => v !== code) : [...value, code]);
  return (
    <div className="flex flex-wrap gap-2" data-testid="action-picker">
      {ACTION_OPTIONS.map((o) => (
        <CheckboxChip
          key={o.code}
          checked={value.includes(o.code)}
          onClick={() => toggle(o.code)}
          label={o.label}
          testid={`action-${o.code}`}
        />
      ))}
    </div>
  );
};

const LossCausePicker = ({ value = "", onChange }) => {
  const isPreset = LOSS_CAUSE_PRESETS.includes(value);
  // Local sticky state so 'Other' remains selected even when the free text is empty.
  // Seeded from value on first render; sync when the incoming value flips to a preset.
  const [mode, setMode] = useState(() =>
    isPreset ? value : value ? "__other__" : ""
  );
  useEffect(() => {
    if (isPreset && mode !== value) setMode(value);
    // Do NOT clobber mode when value becomes empty — that would kick us out of "Other".
  }, [value, isPreset, mode]);

  const pickPreset = (preset) => {
    setMode(preset);
    onChange(preset);
  };
  const pickOther = () => {
    setMode("__other__");
    if (LOSS_CAUSE_PRESETS.includes(value)) onChange(""); // clear preset text so free-text box is blank
  };
  return (
    <div className="flex flex-col gap-2" data-testid="loss-cause-picker">
      <div className="flex flex-wrap gap-2">
        {LOSS_CAUSE_PRESETS.map((preset) => (
          <CheckboxChip
            key={preset}
            checked={mode === preset}
            onClick={() => pickPreset(preset)}
            label={preset}
            testid={`loss-cause-${preset === "Stone damage" ? "stone" : "theft"}`}
          />
        ))}
        <CheckboxChip
          checked={mode === "__other__"}
          onClick={pickOther}
          label="Other"
          testid="loss-cause-other"
        />
      </div>
      {mode === "__other__" && (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Please specify (e.g. hail, vandalism, accident)"
          data-testid="loss-cause-other-input"
          className={`${inputCls} px-3 max-w-md`}
        />
      )}
    </div>
  );
};

const emptyGlassItem = { label: "", width: "", length: "", qty: "1", price: "", notes: "" };

const flatglassLineTotal = (it) =>
  (Number(it?.qty) || 0) * (Number(it?.price) || 0);
const flatglassGrandTotal = (items = []) =>
  items.reduce((sum, it) => sum + flatglassLineTotal(it), 0);

const FlatglassSizesEditor = ({ value = [], onChange }) => {
  const items = value.length ? value : [emptyGlassItem];
  const update = (i, field, v) =>
    onChange(items.map((it, idx) => (idx === i ? { ...it, [field]: v } : it)));
  const add = () => onChange([...items, { ...emptyGlassItem }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="mt-1 bg-[color:var(--brand-tint-bg)] border border-[color:var(--brand-accent)]/40 rounded-md p-4" data-testid="flatglass-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display uppercase tracking-widest text-xs text-[color:var(--brand-accent-strong)]">
          Flatglass sizes
        </div>
        <button
          type="button"
          onClick={add}
          data-testid="flatglass-add-btn"
          className="text-xs font-display uppercase tracking-widest text-[color:var(--brand-accent)] hover:text-[color:var(--brand-accent-strong)] inline-flex items-center gap-1"
        >
          + Add glass
        </button>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div
            key={i}
            className="grid grid-cols-1 sm:grid-cols-[1.4fr_0.9fr_0.9fr_0.5fr_0.9fr_1.2fr_auto] gap-2 sm:items-center pb-3 sm:pb-0 border-b sm:border-b-0 border-[#e5e7eb]"
            data-testid={`flatglass-row-${i}`}
          >
            <input
              placeholder="Label (e.g. Shop window)"
              value={it.label}
              onChange={(e) => update(i, "label", e.target.value)}
              className={`${inputCls} px-3`}
              data-testid={`flatglass-label-${i}`}
            />
            <input
              placeholder="Width (mm)"
              value={it.width}
              onChange={(e) => update(i, "width", e.target.value)}
              className={`${inputCls} px-3`}
              data-testid={`flatglass-width-${i}`}
            />
            <input
              placeholder="Length (mm)"
              value={it.length}
              onChange={(e) => update(i, "length", e.target.value)}
              className={`${inputCls} px-3`}
              data-testid={`flatglass-length-${i}`}
            />
            <input
              placeholder="Qty"
              value={it.qty}
              onChange={(e) => update(i, "qty", e.target.value)}
              className={`${inputCls} px-3`}
              data-testid={`flatglass-qty-${i}`}
            />
            <input
              placeholder="Price ea (R)"
              value={it.price}
              onChange={(e) => update(i, "price", e.target.value)}
              className={`${inputCls} px-3`}
              data-testid={`flatglass-price-${i}`}
            />
            <input
              placeholder="Notes (thickness, tint…)"
              value={it.notes}
              onChange={(e) => update(i, "notes", e.target.value)}
              className={`${inputCls} px-3`}
              data-testid={`flatglass-notes-${i}`}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              disabled={items.length === 1}
              title="Remove"
              data-testid={`flatglass-remove-${i}`}
              className="text-xs h-9 w-9 rounded-md border border-[#cfd7db] text-[#dc2626] hover:bg-[#fef2f2] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs flex-wrap gap-2">
        <p className="text-[color:var(--brand-accent-strong)]/80">
          Add one row per pane. Sizes + prices roll up into the Quote / Invoice totals automatically.
        </p>
        <div
          data-testid="flatglass-subtotal"
          className="font-display uppercase tracking-widest text-[#111619]"
        >
          Flatglass subtotal:{" "}
          <span className="font-bold text-[color:var(--brand-accent)]">
            R {flatglassGrandTotal(items).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      </div>
    </div>
  );
};


const Field = ({ label, hint, children, span = 1 }) => (
  <div className={`flex flex-col gap-1.5 ${span === 2 ? "sm:col-span-2" : ""}`}>
    <Label className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">
      {label}
    </Label>
    {children}
    {hint && <div className="text-[11px] text-[#9aa6ad]">{hint}</div>}
  </div>
);

const Section = ({ title, sub, children }) => (
  <section className="bg-white border border-[#cfd7db] rounded-md p-4 sm:p-6 shadow-sm">
    <div className="mb-5">
      <h2 className="font-display font-bold uppercase tracking-wider text-lg text-[#111619] border-l-4 border-[color:var(--brand-accent)] pl-3">
        {title}
      </h2>
      {sub && <p className="text-xs text-[#3f4b52] mt-1 pl-4">{sub}</p>}
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
      {children}
    </div>
  </section>
);

export const AdminJobForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [customerPrompt, setCustomerPrompt] = useState(null); // {payload}
  const [insurers, setInsurers] = useState([]);

  useEffect(() => {
    // Load insurer register once — used to auto-fill excess % on selection.
    (async () => {
      try {
        setInsurers(await adminApi.listInsurers({ active: true }));
      } catch { /* silent — picker degrades to free-text */ }
    })();
  }, []);

  useEffect(() => {
    if (!isEdit) {
      // On new-job, allow ?customer_id= prefill
      const cid = searchParams.get("customer_id");
      if (cid) {
        (async () => {
          try {
            const c = await adminApi.getCustomer(cid);
            setForm((f) => ({
              ...f,
              customer_id: c.id,
              customer_code: c.code,
              client_name: c.name || f.client_name,
              client_id_number: c.id_or_reg_number || "",
              contact_mobile: c.mobile || "",
              email: c.email || "",
              contact_home: c.telephone || "",
              insurance_company: c.insurer_on_file || "",
              policy_number: c.policy_number_on_file || "",
              broker: c.broker_on_file || "",
            }));
          } catch { /* silent */ }
        })();
      }
      return;
    }
    (async () => {
      try {
        const job = await adminApi.getJob(id);
        setForm({ ...blank, ...job });
      } catch (e) {
        toast.error("Could not load job");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, searchParams]);

  const set = (k) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const setNum = (k) => (e) => {
    // Keep the raw input value (including empty string) — the calc helper
    // coerces `Number(x) || 0` on read, so blank cells are treated as zero
    // without prefilling a distracting "0" into every input.
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const saveJob = async (payload) => {
    setSaving(true);
    try {
      let saved;
      if (isEdit) saved = await adminApi.updateJob(id, payload);
      else saved = await adminApi.createJob(payload);
      toast.success(`Job #${saved.job_number} ${isEdit ? "updated" : "created"}`);
      nav(`/admin/jobs/${saved.id}`);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || "Could not save job";
      toast.error(typeof msg === "string" ? msg : "Could not save");
    } finally {
      setSaving(false);
      setCustomerPrompt(null);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) {
      toast.error("Client name is required.");
      return;
    }
    // Coerce blank numeric inputs to 0 before sending. Keeps the UX clean
    // (empty boxes) while the backend still receives well-typed numbers.
    const numFields = ["cost", "retail", "fitment_fee", "moulding",
                       "discount_pct", "excess_pct", "excess_amount_override", "vat_pct"];
    const coerced = numFields.reduce((acc, k) => {
      acc[k] = form[k] === "" || form[k] == null ? 0 : Number(form[k]);
      return acc;
    }, {});
    let payload = { ...form, ...coerced };
    // Normalize fitment_time free-form input ("08H00", "10;00", "8h30") to canonical "HH:MM".
    // Falls back to the raw value if unparseable, so we never silently drop the fitter's intent.
    if (payload.fitment_time) {
      const cleaned = normalizeTime(payload.fitment_time);
      if (cleaned) payload.fitment_time = cleaned;
    }
    // If no client_id linked but we have a name + reg, persist to the Clients register
    // so next time the same reg auto-fills. Silent on failure — job save still proceeds.
    if (!isEdit && !form.client_id && form.registration && form.client_name.trim()) {
      try {
        const [first_name, ...rest] = form.client_name.trim().split(" ");
        const newClient = await adminApi.createClient({
          first_name: first_name || form.client_name.trim(),
          last_name: rest.join(" ") || null,
          mobile: form.contact_mobile || null,
          email: form.email || null,
          telephone: form.contact_home || null,
          id_number: form.client_id_number || null,
          address: form.fitment_address || null,
          vehicles: [{
            registration: form.registration,
            vin: form.vin_number || null,
            make_model: form.vehicle_make_model || null,
            year: form.vehicle_year || null,
          }],
        });
        const veh = (newClient.vehicles || [])[0] || null;
        payload = { ...payload, client_id: newClient.id, vehicle_id: veh?.id || null };
      } catch { /* silent — job save still proceeds */ }
    }
    if (!isEdit && !form.customer_id) {
      setCustomerPrompt({ payload });
      return;
    }
    saveJob(payload);
  };

  // Called by the modal — creates a new customer from client fields and links it
  const saveAsNewCustomer = async () => {
    if (!customerPrompt) return;
    setSaving(true);
    try {
      const isCompany = /pty|ltd|cc|inc|company|shop|repairs|fleet|group/i.test(form.client_name);
      const newCust = await adminApi.createCustomer({
        name: form.client_name.trim(),
        customer_type: isCompany ? "company" : "individual",
        id_or_reg_number: form.client_id_number || null,
        mobile: form.contact_mobile || null,
        email: form.email || null,
        telephone: form.contact_home || null,
        billing_address: form.fitment_address || null,
        account_terms: form.payment_type === "cash" ? "cod" : "30_days",
        active: true,
      });
      toast.success(`Customer ${newCust.code} created`);
      await saveJob({
        ...customerPrompt.payload,
        customer_id: newCust.id,
        customer_code: newCust.code,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create customer");
      setSaving(false);
    }
  };

  const saveAsCod = () => {
    if (!customerPrompt) return;
    saveJob({ ...customerPrompt.payload, customer_id: null, customer_code: null });
  };

  const openShatterprufe = async () => {
    // Shatterprufe's product catalogue is behind a portal login. We copy the
    // part code (if any) to the admin's clipboard so they can paste it into
    // the Shatterprufe search, and open the catalogue in a new tab.
    const code = (form.part_code || "").trim();
    try {
      if (code && navigator.clipboard) {
        await navigator.clipboard.writeText(code);
        toast.success(`Part code "${code}" copied — paste it into Shatterprufe search`);
      } else if (!code) {
        toast.message("No part code yet — opening Shatterprufe catalogue");
      }
    } catch { /* clipboard denied — still open the tab */ }
    window.open(
      "https://shatterprufe.pg.co.za/SignIn?returnUrl=%2Fproducts%2F",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const calc = calcJob(form);
  const isInsurance = form.payment_type === "insurance";

  if (loading) {
    return <div className="p-10 text-center text-[#3f4b52]" data-testid="job-form-loading">Loading…</div>;
  }

  return (
    <form onSubmit={onSubmit} data-testid="admin-job-form" data-brand={form.brand || "dd"} className="pb-24 sm:pb-0">
      {/* Brand-context banner — makes it visually crystal-clear which letterhead this job will print with. */}
      <div
        data-testid="brand-context-banner"
        className={`mb-4 rounded-md border px-4 py-2 flex items-center gap-3 ${
          form.brand === "bsg"
            ? "bg-[color:var(--brand-tint-bg)] border-[color:var(--brand-accent)]/40"
            : "bg-[color:var(--brand-tint-bg)] border-[color:var(--brand-accent)]/40"
        }`}
      >
        <span className="inline-block h-2.5 w-2.5 rounded-full bg-[color:var(--brand-accent)]" />
        <span className="font-display uppercase tracking-widest text-xs text-[color:var(--brand-accent-strong)]">
          {form.brand === "bsg" ? "BSG Auto Glass paperwork" : "Diesel & Dust paperwork"}
        </span>
        <span className="text-xs text-[#3f4b52] hidden sm:inline">· Change under &quot;Paperwork brand&quot; below</span>
      </div>

      <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link to="/admin/jobs" className="inline-flex items-center gap-2 text-xs font-display uppercase tracking-widest text-[#3f4b52] hover:text-[color:var(--brand-accent)]">
            <ArrowLeft size={14} /> Back to jobs
          </Link>
          <h1 className="mt-2 font-display font-black uppercase text-2xl sm:text-3xl md:text-4xl text-[#111619] break-words">
            {isEdit ? `Edit Job #${form.job_number || ""}` : "New Job"}
          </h1>
          <p className="text-[#3f4b52] mt-1 text-sm sm:text-base max-w-2xl">
            Enter the basic information once. The system generates the Job Card, Claim Form, Insurance Quotation, Excess Invoice, Order Form and Cash Invoice automatically.
          </p>
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="job-form-save"
          className="hidden sm:inline-flex bg-[color:var(--brand-accent)] text-white font-display uppercase tracking-widest text-sm px-6 h-11 rounded-md items-center gap-2 hover:brightness-110 transition shadow"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? "Saving" : (isEdit ? "Save changes" : "Create job")}
        </button>
      </div>

      <div className="mt-8 space-y-6 max-w-5xl">
        <CustomerPicker form={form} setForm={setForm} />

        <ClientLookupCard
          value={{ client_id: form.client_id, vehicle_id: form.vehicle_id }}
          registration={form.registration}
          vin={form.vin_number}
          onMatch={(cl, veh) => {
            if (!cl) {
              // Unlink
              setForm((f) => ({ ...f, client_id: null, vehicle_id: null }));
              return;
            }
            setForm((f) => ({
              ...f,
              client_id: cl.id,
              vehicle_id: veh?.id || null,
              client_name: (cl.full_name || `${cl.first_name || ""} ${cl.last_name || ""}`).trim() || f.client_name,
              client_id_number: cl.id_number || f.client_id_number,
              contact_mobile: cl.mobile || f.contact_mobile,
              email: cl.email || f.email,
              contact_home: cl.telephone || f.contact_home,
              fitment_address: cl.address || f.fitment_address,
              vehicle_make_model: veh?.make_model || f.vehicle_make_model,
              vehicle_year: veh?.year || f.vehicle_year,
              registration: veh?.registration || f.registration,
              vin_number: veh?.vin || f.vin_number,
            }));
          }}
        />

        <Section title="Paperwork brand" sub="Which logo appears on the Quote, Invoice, Claim Form, Job Card, Excess Invoice — for THIS job only. Numbering + banking stay the same.">
          <Field label="Brand" span={3}>
            <div className="flex gap-2 flex-wrap" data-testid="brand-picker">
              {[
                { code: "dd", label: "Diesel & Dust" },
                { code: "bsg", label: "BSG Auto Glass" },
              ].map((o) => (
                <button
                  key={o.code}
                  type="button"
                  data-testid={`brand-${o.code}`}
                  onClick={() => setForm((f) => ({ ...f, brand: o.code }))}
                  className={`px-4 h-10 rounded-md border font-display uppercase tracking-widest text-xs transition ${
                    form.brand === o.code
                      ? (o.code === "bsg"
                          ? "bg-[#1d4ed8] text-white border-[#1d4ed8] shadow-sm"
                          : "bg-[#f28a16] text-white border-[#f28a16] shadow-sm")
                      : "bg-white text-[#3f4b52] border-[#cfd7db] hover:border-[color:var(--brand-accent)] hover:text-[color:var(--brand-accent)]"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </Field>
        </Section>

        <Section title="Client details" sub="Person or company we're invoicing / repairing for.">
          <Field label="Client name / company *">
            <Input value={form.client_name} onChange={set("client_name")} className={inputCls} data-testid="f-client-name" />
          </Field>
          <Field label="Title & surname" hint="Individual person on the vehicle (e.g. Mr J. Smith).">
            <Input value={form.client_title_surname} onChange={set("client_title_surname")} className={inputCls} data-testid="f-client-title-surname" placeholder="e.g. Mr J. Smith" />
          </Field>
          <Field label="ID / company number">
            <Input value={form.client_id_number} onChange={set("client_id_number")} className={inputCls} data-testid="f-client-id" />
          </Field>
          <Field label="Mobile">
            <Input value={form.contact_mobile} onChange={set("contact_mobile")} className={inputCls} data-testid="f-mobile" />
          </Field>
          <Field label="Email">
            <Input type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="f-email" />
          </Field>
          <Field label="Home phone">
            <Input value={form.contact_home} onChange={set("contact_home")} className={inputCls} />
          </Field>
          <Field label="Work phone">
            <Input value={form.contact_work} onChange={set("contact_work")} className={inputCls} />
          </Field>
          <Field label="Fitment address" span={2}>
            <Input value={form.fitment_address} onChange={set("fitment_address")} className={inputCls} data-testid="f-address" />
          </Field>
          <Field label="GPS coordinates (optional)" span={2}>
            <Input value={form.fitment_gps} onChange={set("fitment_gps")} className={inputCls} placeholder="-25.1234, 28.5678" />
          </Field>
        </Section>

        <Section title="Vehicle details" sub="Look up the correct glass part number in the Shatterprufe catalogue.">
          <Field label="Vehicle make & model">
            <CopyableInput value={form.vehicle_make_model} onChange={set("vehicle_make_model")} className={inputCls} testid="f-vehicle" />
          </Field>
          <Field label="Year model">
            <CopyableInput value={form.vehicle_year} onChange={set("vehicle_year")} className={inputCls} testid="f-year" />
          </Field>
          <Field label="Registration">
            <CopyableInput value={form.registration} onChange={set("registration")} className={inputCls} testid="f-reg" />
          </Field>
          <Field label="Engine number">
            <CopyableInput value={form.engine_number} onChange={set("engine_number")} className={inputCls} testid="f-engine" />
          </Field>
          <Field label="VIN number">
            <CopyableInput value={form.vin_number} onChange={set("vin_number")} className={inputCls} testid="f-vin" />
          </Field>
          <Field label="Glass part code" hint="Copies the code to your clipboard and opens the Shatterprufe catalogue.">
            <div className="flex gap-2">
              <CopyableInput value={form.part_code} onChange={set("part_code")} className={inputCls + " flex-1"} testid="f-partcode" placeholder="e.g. BM8054AGN" />
              <button
                type="button"
                onClick={openShatterprufe}
                data-testid="f-shatterprufe-lookup"
                className="px-3 h-10 bg-[#20282c] text-white text-xs font-display uppercase tracking-wider rounded-md inline-flex items-center gap-1.5 hover:brightness-110 whitespace-nowrap"
              >
                <ExternalLink size={14} /> Shatterprufe
              </button>
            </div>
          </Field>
        </Section>

        <Section title="Payment type & insurance">
          <Field label="Payment type *">
            <select value={form.payment_type} onChange={set("payment_type")} className={`${inputCls} px-3`} data-testid="f-payment-type">
              {PAYMENT_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={set("status")} className={`${inputCls} px-3`} data-testid="f-status">
              {JOB_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>

          {isInsurance && (
            <>
              <Field label="Insurance company" hint={insurers.length ? "Start typing to pick from the Insurer register — excess % auto-fills." : "Add insurers under Admin → Insurers to auto-fill excess %."}>
                <Input
                  value={form.insurance_company}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Look up a matching insurer (case-insensitive) — if found,
                    // pull its default excess % / amount into the pricing fields
                    // (only when those are currently blank, so we don't clobber
                    // a bespoke figure the admin already typed).
                    const match = insurers.find(
                      (i) => (i.name || "").toLowerCase() === val.toLowerCase(),
                    );
                    setForm((f) => {
                      const next = { ...f, insurance_company: val };
                      if (match) {
                        // Only auto-fill when the input is *empty* — a bespoke
                        // zero-excess job stays at 0 rather than getting rewritten.
                        if (f.excess_pct === "" || f.excess_pct == null) {
                          next.excess_pct = match.default_excess_pct || "";
                        }
                        if ((f.excess_amount_override === "" || f.excess_amount_override == null)
                            && match.default_excess_amount) {
                          next.excess_amount_override = match.default_excess_amount;
                        }
                      }
                      return next;
                    });
                  }}
                  list="insurer-suggestions"
                  className={inputCls}
                  data-testid="f-insurance"
                />
                <datalist id="insurer-suggestions">
                  {insurers.map((i) => (
                    <option key={i.id} value={i.name}>
                      {`Excess ${Number(i.default_excess_pct || 0).toFixed(1)}%${i.default_excess_amount ? ` · R${i.default_excess_amount}` : ""}`}
                    </option>
                  ))}
                </datalist>
              </Field>
              <Field label="Broker (optional — pick from register or create new)" span={2}>
                <BrokerPicker
                  value={form._broker_obj || (form.broker ? { name: form.broker, contact_person: form.broker_contact, id: form.broker_id } : null)}
                  onChange={(b) => setForm((f) => ({
                    ...f,
                    broker_id: b?.id || null,
                    broker: b?.name || "",
                    broker_contact: b?.contact_person || "",
                    broker_fax: b?.phone || f.broker_fax,
                    _broker_obj: b,
                  }))}
                  testid="f-broker-picker"
                />
              </Field>
              <Field label="Policy number">
                <Input value={form.policy_number} onChange={set("policy_number")} className={inputCls} data-testid="f-policy" />
              </Field>
              <Field label="Claim number">
                <Input value={form.claim_number} onChange={set("claim_number")} className={inputCls} data-testid="f-claim" />
              </Field>
              <Field label="Authorised by">
                <Input value={form.authorized_by} onChange={set("authorized_by")} className={inputCls} />
              </Field>
              <Field label="Date of loss">
                <Input type="date" value={form.date_of_loss} onChange={set("date_of_loss")} className={inputCls} />
              </Field>
              <Field label="Damage location" span={2} hint="Tick all that apply. Shown on Claim Form.">
                <DamageLocationPicker
                  value={form.damage_locations}
                  onChange={(v) => setForm((f) => ({ ...f, damage_locations: v }))}
                />
              </Field>
              {(form.damage_locations || []).includes("flatglass") && (
                <Field label="Flatglass items" span={2} hint="One row per pane. Add more with the + button.">
                  <FlatglassSizesEditor
                    value={form.flatglass_items}
                    onChange={(v) => setForm((f) => ({ ...f, flatglass_items: v }))}
                  />
                </Field>
              )}
              <Field label="Action" span={2} hint="Tick Replace, Repair, or both.">
                <ActionPicker
                  value={form.claim_actions}
                  onChange={(v) => setForm((f) => ({ ...f, claim_actions: v }))}
                />
              </Field>
              <Field label="Loss cause" span={2}>
                <LossCausePicker
                  value={form.cause_of_loss || ""}
                  onChange={(v) => setForm((f) => ({ ...f, cause_of_loss: v }))}
                />
              </Field>
            </>
          )}
        </Section>

        <Section title="Fitment scheduling">
          <Field label="Fitment date">
            <Input type="date" value={form.fitment_date} onChange={set("fitment_date")} className={inputCls} data-testid="f-fitment-date" />
          </Field>
          <Field label="Fitment time">
            <Input value={form.fitment_time} onChange={set("fitment_time")} className={inputCls} placeholder="08H00" />
          </Field>
          <Field label="Fitter / team leader">
            <Input value={form.fitter_team_leader} onChange={set("fitter_team_leader")} className={inputCls} />
          </Field>
          <Field label="Assistant">
            <Input value={form.assistant} onChange={set("assistant")} className={inputCls} />
          </Field>
        </Section>

        <Section title="Pricing" sub="Set VAT mode, add-ons and discount lines. All amounts in ZAR.">
          <Field label="VAT status">
            <select value={form.vat_mode} onChange={set("vat_mode")} className={`${inputCls} px-3`} data-testid="f-vat-mode">
              <option value="exclusive">VAT exclusive — add VAT on top</option>
              <option value="inclusive">VAT inclusive — VAT already in price</option>
              <option value="none">No VAT — zero-rated / exempt</option>
            </select>
          </Field>
          <Field label="VAT %">
            <Input type="number" step="0.01" value={form.vat_pct} onChange={setNum("vat_pct")} className={inputCls} disabled={form.vat_mode === "none"} />
          </Field>
          <Field label="Cost (internal)">
            <Input type="number" step="0.01" value={form.cost} onChange={setNum("cost")} className={inputCls} />
          </Field>
          <Field label="Retail (glass)">
            <Input type="number" step="0.01" value={form.retail} onChange={setNum("retail")} className={inputCls} data-testid="f-retail" />
          </Field>
          <Field label="Fitment fee">
            <Input type="number" step="0.01" value={form.fitment_fee} onChange={setNum("fitment_fee")} className={inputCls} />
          </Field>
          <Field label="Moulding">
            <Input type="number" step="0.01" value={form.moulding} onChange={setNum("moulding")} className={inputCls} />
          </Field>
          <Field label="Discount %">
            <Input type="number" step="0.01" value={form.discount_pct} onChange={setNum("discount_pct")} className={inputCls} />
          </Field>
          {isInsurance ? (
            <Field label="Excess %">
              <Input data-testid="f-excess-pct" type="number" step="0.01" value={form.excess_pct} onChange={setNum("excess_pct")} className={inputCls} />
            </Field>
          ) : (
            <Field label="Excess amount (override)">
              <Input data-testid="f-excess-amount" type="number" step="0.01" value={form.excess_amount_override} onChange={setNum("excess_amount_override")} className={inputCls} />
            </Field>
          )}

          {/* Extra line items */}
          <div className="sm:col-span-2 pt-2">
            <div className="flex items-center justify-between mb-2">
              <Label className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">Extra line items (add-ons, discounts, call-out)</Label>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, extra_line_items: [...(f.extra_line_items || []), { description: "", qty: 1, unit_price: 0, is_discount: false }] }))}
                data-testid="f-add-extra"
                className="text-[color:var(--brand-accent)] hover:underline text-xs font-display uppercase tracking-widest inline-flex items-center gap-1"
              >
                + Add line
              </button>
            </div>
            {(form.extra_line_items || []).length === 0 ? (
              <div className="text-xs text-[#9aa6ad] italic bg-[#f5f6f6] rounded-md py-3 px-4 border border-dashed border-[#cfd7db]">No extra lines. Use these for a call-out fee, second-moulding, extra parts or an ad-hoc discount.</div>
            ) : (
              <div className="space-y-2">
                {(form.extra_line_items || []).map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`f-extra-row-${idx}`}>
                    <Input
                      value={it.description}
                      onChange={(e) => setForm((f) => ({ ...f, extra_line_items: f.extra_line_items.map((x, i) => i === idx ? { ...x, description: e.target.value } : x) }))}
                      placeholder={it.is_discount ? "Discount description (e.g. Loyalty)" : "Description (e.g. Call-out fee)"}
                      className={`${inputCls} col-span-6`}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      value={it.qty}
                      onChange={(e) => setForm((f) => ({ ...f, extra_line_items: f.extra_line_items.map((x, i) => i === idx ? { ...x, qty: Number(e.target.value || 0) } : x) }))}
                      placeholder="Qty"
                      className={`${inputCls} col-span-1 text-right`}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      value={it.unit_price}
                      onChange={(e) => setForm((f) => ({ ...f, extra_line_items: f.extra_line_items.map((x, i) => i === idx ? { ...x, unit_price: Number(e.target.value || 0) } : x) }))}
                      placeholder="Unit price"
                      className={`${inputCls} col-span-2 text-right`}
                    />
                    <label className="col-span-2 inline-flex items-center gap-1.5 text-xs text-[#3f4b52]">
                      <input type="checkbox" checked={it.is_discount} onChange={(e) => setForm((f) => ({ ...f, extra_line_items: f.extra_line_items.map((x, i) => i === idx ? { ...x, is_discount: e.target.checked } : x) }))} className="h-4 w-4 accent-[#f28a16]" />
                      Discount
                    </label>
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, extra_line_items: f.extra_line_items.filter((_, i) => i !== idx) }))}
                      className="col-span-1 text-[#dc2626] text-xs justify-self-center"
                      aria-label="Remove line"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Section>

        <Section title="Invoice & order tracking">
          <Field label="Order number">
            <Input value={form.order_number} onChange={set("order_number")} className={inputCls} />
          </Field>
          <Field label="Invoice sent to">
            <Input value={form.invoice_sent_to} onChange={set("invoice_sent_to")} className={inputCls} placeholder="Broker / insurer email" />
          </Field>
          <Field label="Invoice status">
            <select value={form.invoice_status} onChange={set("invoice_status")} className={`${inputCls} px-3`} data-testid="f-invoice-status">
              {INVOICE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Payment received">
            <label className="inline-flex items-center gap-2 h-10 px-3 bg-white border border-[#cfd7db] rounded-md">
              <input
                type="checkbox"
                checked={form.payment_received}
                onChange={(e) => set("payment_received")(e.target.checked)}
                className="h-4 w-4 accent-[#f28a16]"
                data-testid="f-payment-received"
              />
              <span className="text-sm text-[#111619]">Yes, paid</span>
            </label>
          </Field>
        </Section>

        <Section title="Notes" sub="Anything the team or admin needs to know.">
          <div className="sm:col-span-2">
            <Textarea
              value={form.notes ?? ""}
              onChange={set("notes")}
              rows={4}
              className={`${inputCls} h-auto py-3`}
              placeholder="Additional notes about the vehicle, fitment conditions, special requests…"
              data-testid="f-notes"
            />
          </div>
        </Section>

        {/* Live pricing preview */}
        <div className="bg-[#20282c] text-white border border-[#20282c] rounded-md p-6 shadow-sm">
          <div className="font-display uppercase tracking-widest text-xs text-[color:var(--brand-highlight)] mb-3">
            Live pricing preview · {form.vat_mode === "inclusive" ? "VAT inclusive" : form.vat_mode === "none" ? "No VAT" : "VAT exclusive"}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-sm">
            <div>Lines subtotal:<br /><b>{money(calc.grossLineSubtotal)}</b></div>
            <div>Discounts:<br /><b>-{money(calc.discountAmount)}</b></div>
            <div>Net (excl. VAT):<br /><b>{money(calc.subAfterDiscount)}</b></div>
            <div>VAT{form.vat_mode !== "none" ? ` (${form.vat_pct}%)` : ""}:<br /><b>{money(calc.vatAmount)}</b></div>
            <div>Gross total:<br /><b>{money(calc.inclusiveSubtotal)}</b></div>
            {isInsurance && (
              <>
                <div>Excess ({form.excess_pct}%):<br /><b className="text-[color:var(--brand-highlight)]">{money(calc.excessAmount)}</b></div>
                <div>Insurance total:<br /><b className="text-[color:var(--brand-highlight)]">{money(calc.insuranceTotal)}</b></div>
              </>
            )}
            {!isInsurance && (
              <div>Customer pays:<br /><b className="text-[color:var(--brand-highlight)]">{money(calc.inclusiveSubtotal)}</b></div>
            )}
            <div>Gross profit:<br /><b>{money(calc.grossProfit)}</b></div>
          </div>
        </div>
      </div>

      <div className="mt-8 max-w-5xl hidden sm:flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="bg-[color:var(--brand-accent)] text-white font-display uppercase tracking-widest text-sm px-7 h-12 rounded-md inline-flex items-center gap-2 hover:brightness-110 transition shadow"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? "Saving" : (isEdit ? "Save changes" : "Create job")}
        </button>
      </div>

      {/* Sticky Save bar on mobile — always reachable without scrolling to the bottom. */}
      <div
        className="sm:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-[#cfd7db] p-3 shadow-[0_-4px_10px_rgba(0,0,0,0.06)]"
        data-testid="job-form-save-mobile-bar"
      >
        <button
          type="submit"
          disabled={saving}
          data-testid="job-form-save-mobile"
          className="w-full bg-[color:var(--brand-accent)] text-white font-display uppercase tracking-widest text-sm h-12 rounded-md inline-flex items-center justify-center gap-2 hover:brightness-110 transition shadow disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? "Saving" : (isEdit ? "Save changes" : "Create job")}
        </button>
      </div>

      {customerPrompt && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="customer-prompt-modal">
          <div className="bg-white rounded-md shadow-xl max-w-lg w-full p-6">
            <div className="font-display uppercase tracking-widest text-xs text-[color:var(--brand-accent)]">Save without a customer account?</div>
            <h2 className="font-display font-black uppercase text-xl text-[#111619] mt-1">How should we file this job?</h2>
            <p className="text-sm text-[#3f4b52] mt-2">
              You didn&apos;t attach a customer. For statements and reports later, it helps if there&apos;s a record.
              Pick one:
            </p>

            <div className="mt-5 space-y-2">
              <button
                type="button"
                onClick={saveAsNewCustomer}
                disabled={saving}
                data-testid="prompt-create-customer"
                className="w-full bg-[color:var(--brand-accent)] text-white rounded-md py-3 px-4 font-display uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Create customer &amp; link it (recommended)
              </button>
              <button
                type="button"
                onClick={saveAsCod}
                disabled={saving}
                data-testid="prompt-save-cod"
                className="w-full bg-white border border-[#cfd7db] text-[#111619] rounded-md py-3 px-4 font-display uppercase tracking-widest text-xs"
              >
                Save as walk-in / COD only (no customer record)
              </button>
              <button
                type="button"
                onClick={() => setCustomerPrompt(null)}
                className="w-full text-[#3f4b52] text-xs py-2 hover:underline"
              >
                Cancel — I want to attach an existing customer first
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default AdminJobForm;
